import { useParams, Link, Navigate } from "react-router-dom";
import { blogPosts } from "@/data/blogPosts";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Clock,
  User,
  CheckCircle2,
  Phone,
  Star,
  MapPin,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEOHead } from "@/components/SEOHead";

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find((p) => p.slug === slug);
  const relatedPosts = post
    ? blogPosts.filter((p) => p.id !== post.id).slice(0, 3)
    : [];

  const isPriceArticle =
    post?.slug === "was-kostet-eine-haushaltsaufloesung-berlin" ||
    post?.slug === "was-kostet-eine-entruempelung-berlin";
  const isTodesfallArticle =
    post?.slug === "haushaltsaufloesung-nach-todesfall-checkliste" ||
    post?.slug === "haushaltsaufloesung-nach-todesfall-schritt-fuer-schritt" ||
    post?.slug === "wohnung-aufloesen-als-erbe-erste-schritte";
  const isSelbermachenArticle =
    post?.slug === "entruempelung-selber-machen-oder-firma";

  const articleSchema = post
    ? {
        "@context": "https://schema.org",
        "@type": "Article",
        "@id": `https://miros-haushaltsaufloesung.de/ratgeber/${post.slug}`,
        headline: post.title,
        description: post.metaDesc,
        image: {
          "@type": "ImageObject",
          url: post.imageUrl,
          width: 1200,
          height: 630,
        },
        author: {
          "@type": "Person",
          name: "Miroslav Ozegovic",
          jobTitle: "Inhaber",
          worksFor: {
            "@type": "LocalBusiness",
            name: "Miros Haushaltsauflösung",
          },
        },
        publisher: {
          "@type": "Organization",
          name: "Miros Haushaltsauflösung",
          url: "https://miros-haushaltsaufloesung.de",
          logo: {
            "@type": "ImageObject",
            url: "https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/698f5d79c6490f2e4d428a9e.png",
            width: 300,
            height: 80,
          },
        },
        datePublished: "2026-01-15",
        dateModified: "2026-06-25",
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `https://miros-haushaltsaufloesung.de/ratgeber/${post.slug}`,
        },
        inLanguage: "de-DE",
        about: {
          "@type": "Thing",
          name: "Haushaltsauflösung Berlin",
        },
      }
    : {};

  const howToSchema = post
    ? {
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: "So läuft eine Haushaltsauflösung in Berlin ab",
        description:
          "Schritt-für-Schritt-Anleitung für eine Haushaltsauflösung in Berlin – von der Besichtigung bis zur besenreinen Übergabe.",
        totalTime: "P7D",
        estimatedCost: {
          "@type": "MonetaryAmount",
          currency: "EUR",
          value: "199-1500",
        },
        step: [
          {
            "@type": "HowToStep",
            position: 1,
            name: "Kostenloser Besichtigungstermin",
            text: "Kontaktieren Sie Miros Haushaltsauflösung. Ein Besichtigungstermin ist oft innerhalb von 24 Stunden möglich. Der Anbieter sieht sich Wohnung und Hausrat an und erkennt verwertbare Gegenstände.",
          },
          {
            "@type": "HowToStep",
            position: 2,
            name: "Verbindliches Festpreisangebot erhalten",
            text: "Nach der Besichtigung erhalten Sie ein schriftliches Festpreisangebot inklusive Wertanrechnung, Entsorgungskosten, Halteverbot und Personal. Kein Nachschlag möglich.",
          },
          {
            "@type": "HowToStep",
            position: 3,
            name: "Räumung zum vereinbarten Termin",
            text: "Das Team erscheint pünktlich und räumt die Wohnung vollständig. Sie müssen nicht anwesend sein. Halteverbotszone wird eigenständig beantragt.",
          },
          {
            "@type": "HowToStep",
            position: 4,
            name: "Besenreine Übergabe",
            text: "Die Wohnung wird besenrein an Sie, den Vermieter oder Käufer übergeben. Alle Materialien wurden umweltgerecht entsorgt.",
          },
        ],
      }
    : {};

  const extractFAQs = (content: string) => {
    const faqs = [];
    const detailsRegex =
      /<details[^>]*>[\s\S]*?<summary[^>]*>[\s\S]*?<h3[^>]*>(.*?)<\/h3>[\s\S]*?<\/summary>[\s\S]*?<p[^>]*>(.*?)<\/p>[\s\S]*?<\/details>/gi;
    let match;
    while ((match = detailsRegex.exec(content)) !== null) {
      faqs.push({
        "@type": "Question",
        name: match[1].replace(/<[^>]*>?/gm, "").trim(),
        acceptedAnswer: {
          "@type": "Answer",
          text: match[2].replace(/<[^>]*>?/gm, "").trim(),
        },
      });
    }
    return faqs;
  };

  const extractedFaqs = post ? extractFAQs(post.content) : [];

  const faqSchema =
    extractedFaqs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: extractedFaqs,
        }
      : null;

  const serviceSchemaForPriceArticle = isPriceArticle
    ? {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Haushaltsauflösung Berlin",
        provider: {
          "@type": "LocalBusiness",
          name: "Miros Haushaltsauflösung",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Hauptstraße 43-44",
            addressLocality: "Berlin",
            postalCode: "13127",
            addressCountry: "DE",
          },
          telephone: "+4915253181061",
          url: "https://miros-haushaltsaufloesung.de",
        },
        areaServed: {
          "@type": "City",
          name: "Berlin",
        },
        description:
          "Professionelle Haushaltsauflösung in Berlin zum Festpreis. Inkl. Wertanrechnung, Entsorgung und besenreiner Übergabe.",
        offers: {
          "@type": "AggregateOffer",
          lowPrice: "199",
          highPrice: "1500",
          priceCurrency: "EUR",
          offerCount: "500",
        },
      }
    : null;

  const howToTodesfallSchema = isTodesfallArticle
    ? {
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: "Haushaltsauflösung nach Todesfall: Schritt-für-Schritt-Checkliste",
        description:
          "Was nach einem Todesfall zu erledigen ist – von der Sterbeurkunde bis zur besenreinen Übergabe der Wohnung in Berlin.",
        totalTime: "P90D",
        step: [
          {
            "@type": "HowToStep",
            position: 1,
            name: "Sterbeurkunde beantragen",
            text: "Mehrere Ausfertigungen beim Standesamt Berlin beantragen. Bearbeitungszeit 2 bis 5 Werktage.",
          },
          {
            "@type": "HowToStep",
            position: 2,
            name: "Testament einreichen und Erbschaft klären",
            text: "Testament beim Nachlassgericht einreichen. Innerhalb von 6 Wochen die Erbschaft annehmen oder ausschlagen.",
          },
          {
            "@type": "HowToStep",
            position: 3,
            name: "Dokumente sichern und Verträge kündigen",
            text: "Wichtige Dokumente sichern, Mietvertrag kündigen, Versorgungsverträge kündigen, Zählerstände notieren.",
          },
          {
            "@type": "HowToStep",
            position: 4,
            name: "Persönliche Erinnerungsstücke sichern",
            text: "Fotos, Briefe und Dokumente aus der Wohnung holen. Möbel die übernommen werden mit Klebezettel kennzeichnen.",
          },
          {
            "@type": "HowToStep",
            position: 5,
            name: "Fachfirma beauftragen und Wohnung besenrein übergeben",
            text: "Kostenlose Besichtigung durch Miros Haushaltsauflösung, verbindliches Festpreisangebot erhalten, Räumungstermin vereinbaren und Wohnung besenrein übergeben.",
          },
        ],
      }
    : null;

  const breadcrumbSchema = post
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Startseite",
            item: "https://miros-haushaltsaufloesung.de",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Ratgeber",
            item: "https://miros-haushaltsaufloesung.de/ratgeber",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: post.title,
            item: `https://miros-haushaltsaufloesung.de/ratgeber/${post.slug}`,
          },
        ],
      }
    : {};

  // suppress unused variable warning
  void isSelbermachenArticle;
  void howToSchema;

  const schemas = post
    ? [
        articleSchema,
        breadcrumbSchema,
        faqSchema,
        ...(isTodesfallArticle ? [howToTodesfallSchema] : []),
        ...(serviceSchemaForPriceArticle ? [serviceSchemaForPriceArticle] : []),
      ].filter(Boolean)
    : [];

  if (!post) {
    return <Navigate to="/ratgeber" replace />;
  }

  return (
    <>
      <SEOHead
        title={
          post ? `${post.title} | Miros Haushaltsauflösung Berlin` : "Ratgeber"
        }
        description={post?.metaDesc || ""}
        canonical={`https://miros-haushaltsaufloesung.de/ratgeber/${slug}`}
        schemaJson={schemas}
        ogImage={post?.imageUrl}
      />
      <div className="flex flex-col w-full bg-background">
        {/* Hero Section */}
        <section className="pt-28 pb-12 md:pt-36 md:pb-16 bg-[#0c2340] text-white">
          <div className="container mx-auto px-4 md:px-6 max-w-6xl">
            {/* Breadcrumb */}
            <nav aria-label="Breadcrumb" className="mb-6">
              <ol className="flex flex-wrap items-center gap-2 text-xs text-white/60">
                <li>
                  <Link to="/" className="hover:text-white transition-colors">
                    Startseite
                  </Link>
                </li>
                <li className="text-white/30">/</li>
                <li>
                  <Link
                    to="/ratgeber"
                    className="hover:text-white transition-colors"
                  >
                    Ratgeber
                  </Link>
                </li>
                <li className="text-white/30">/</li>
                <li className="text-white/80 line-clamp-1">{post.title}</li>
              </ol>
            </nav>

            <div className="flex flex-col items-start gap-4 mb-5">
              <Link
                to="/ratgeber"
                className="inline-flex items-center text-white/70 hover:text-white transition-colors text-sm font-medium"
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> Zurück zur Übersicht
              </Link>

              <div className="inline-block bg-accent text-accent-foreground px-3 py-1 text-xs font-bold uppercase rounded-sm">
                {post.category}
              </div>
            </div>

            <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-black mb-6 leading-tight max-w-4xl">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-5 text-sm text-white/70 font-medium">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 shrink-0" />
                <span>{post.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 shrink-0" />
                <time dateTime="2026-05-15">{post.date}</time>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 shrink-0" />
                <span>{post.readTime}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Content + Sidebar */}
        <section className="py-12 md:py-16">
          <div className="container mx-auto px-4 md:px-6 max-w-6xl">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-14 items-start">
              {/* ── MAIN CONTENT ── */}
              <main className="w-full lg:w-0 lg:flex-1 min-w-0">
                {/* Hero Image */}
                <div className="rounded-sm overflow-hidden mb-10 shadow-md border border-border">
                  <img
                    src={post.imageUrl}
                    alt={`${post.title} – Miros Haushaltsauflösung Berlin`}
                    className="w-full h-auto max-h-[480px] object-cover"
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    width={900}
                    height={480}
                  />
                </div>

                {/* Article Body */}
                <div
                  className="prose prose-xl max-w-none
                  prose-headings:font-heading prose-headings:font-black prose-headings:text-foreground
                  prose-h2:text-3xl prose-h2:md:text-4xl prose-h2:mt-14 prose-h2:mb-6
                  prose-h3:text-2xl prose-h3:md:text-3xl prose-h3:mt-10 prose-h3:mb-4
                  prose-p:text-lg prose-p:md:text-xl prose-p:text-muted-foreground prose-p:leading-loose prose-p:mb-8
                  prose-li:text-lg prose-li:md:text-xl prose-li:text-muted-foreground prose-li:leading-loose
                  prose-ul:my-6 prose-ol:my-6
                  prose-strong:text-foreground
                  prose-a:text-primary prose-a:underline hover:prose-a:text-accent
                  prose-blockquote:border-l-4 prose-blockquote:border-accent prose-blockquote:pl-6 prose-blockquote:italic prose-blockquote:text-muted-foreground"
                  dangerouslySetInnerHTML={{ __html: post.content }}
                />

                {/* ── AUTHOR BOX ── */}
                <div className="mt-14 p-6 md:p-8 rounded-sm border border-border bg-secondary/20 flex flex-col sm:flex-row gap-6 items-start">
                  <div className="shrink-0">
                    <img
                      src="https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a900e659bec99fc9f3afb.jpg"
                      alt="Miroslav Ozegovic – Inhaber Miros Haushaltsauflösung"
                      className="w-20 h-20 rounded-full object-cover border-2 border-accent"
                      loading="lazy"
                      decoding="async"
                      width={80}
                      height={80}
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold uppercase text-accent tracking-wider mb-1">
                      Über den Autor
                    </p>
                    <h3 className="text-lg font-bold text-foreground mb-1">
                      Miroslav Ozegovic
                    </h3>
                    <p className="text-sm text-muted-foreground font-medium mb-2">
                      Inhaber · Miros Haushaltsauflösung Berlin
                    </p>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Miroslav Ozegovic betreibt Miros Haushaltsauflösung seit
                      über 5 Jahren und hat in dieser Zeit mehr als 500
                      Haushalte in Berlin und Umgebung professionell aufgelöst.
                      Sein Fokus liegt auf diskreter, respektvoller Arbeit –
                      gerade in sensiblen Situationen wie nach einem Todesfall.
                    </p>
                    <div className="flex items-center gap-2 mt-3">
                      <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="text-xs text-muted-foreground">
                        Hauptstraße 43-44, 13127 Berlin
                      </span>
                    </div>
                  </div>
                </div>

                {/* ── END-OF-ARTICLE CTA BOX ── */}
                <div className="mt-10 rounded-sm overflow-hidden border border-accent/30 shadow-md">
                  <div className="bg-[#0c2340] text-white p-6 md:p-8">
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className="h-4 w-4 text-accent fill-accent"
                          />
                        ))}
                      </div>
                      <span className="text-sm text-white/80 font-medium">
                        Über 500 abgeschlossene Aufträge in Berlin
                      </span>
                    </div>
                    <h3 className="text-xl md:text-2xl font-heading font-bold mb-3 leading-tight">
                      Kostenlose Besichtigung vereinbaren –{" "}
                      <span className="text-accent">noch heute</span>
                    </h3>
                    <p className="text-white/80 text-sm leading-relaxed mb-6">
                      Kein Risiko, keine Verpflichtung. Nach der kostenlosen
                      Besichtigung erhalten Sie einen verbindlichen Festpreis.
                      Wir melden uns noch am selben Tag.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6 text-sm">
                      {[
                        "Festpreisgarantie",
                        "Vollversichert",
                        "Termine in 48h",
                      ].map((item, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-accent shrink-0" />
                          <span className="text-white/90">{item}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button
                        size="lg"
                        className="bg-accent text-accent-foreground hover:bg-accent/90 font-bold h-12 px-6 flex-1"
                        asChild
                      >
                        <Link to="/kontakt">Jetzt Angebot anfordern</Link>
                      </Button>
                      <Button
                        size="lg"
                        className="bg-transparent border border-white/30 text-white hover:bg-white/10 hover:text-white font-bold h-12 px-6 flex-1"
                        asChild
                      >
                        <a href="tel:015253181061">
                          <Phone className="h-4 w-4 mr-2" /> 01525 3181061
                        </a>
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Share Buttons */}
                <div className="mt-10 pt-8 border-t border-border">
                  <p className="text-sm font-bold text-foreground mb-4">
                    Artikel teilen:
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-sm text-xs"
                      onClick={() =>
                        window.open(
                          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`,
                          "_blank",
                        )
                      }
                    >
                      Facebook
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-sm text-xs"
                      onClick={() =>
                        window.open(
                          `https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post.title)}`,
                          "_blank",
                        )
                      }
                    >
                      Twitter / X
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-sm text-xs"
                      onClick={() =>
                        window.open(
                          `https://wa.me/?text=${encodeURIComponent(post.title + " " + window.location.href)}`,
                          "_blank",
                        )
                      }
                    >
                      WhatsApp
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-sm text-xs"
                      onClick={() =>
                        window.open(
                          `mailto:?subject=${encodeURIComponent(post.title)}&body=${encodeURIComponent("Dieser Artikel könnte dich interessieren: " + window.location.href)}`,
                        )
                      }
                    >
                      E-Mail
                    </Button>
                  </div>
                </div>
              </main>

              {/* ── SIDEBAR ── */}
              <aside className="w-full lg:w-80 xl:w-96 shrink-0">
                <div className="lg:sticky lg:top-28 space-y-6">
                  {/* Contact Box */}
                  <div className="bg-[#0c2340] text-white p-6 rounded-sm shadow-md">
                    <p className="text-xs font-bold uppercase text-accent tracking-wider mb-3">
                      Direkte Hilfe
                    </p>
                    <h3 className="text-lg font-bold mb-3 leading-snug">
                      Brauchen Sie Hilfe bei Ihrer Haushaltsauflösung?
                    </h3>
                    <p className="text-sm text-white/75 mb-5 leading-relaxed">
                      Wir übernehmen Ihre Haushaltsauflösung in Berlin –
                      schnell, diskret und zum Festpreis. Kostenlose
                      Besichtigung innerhalb von 48h möglich.
                    </p>
                    <ul className="space-y-2.5 mb-5">
                      {[
                        "Kostenlose Besichtigung",
                        "Verbindlicher Festpreis",
                        "Wertanrechnung möglich",
                        "Besenreine Übergabe",
                      ].map((item, i) => (
                        <li
                          key={i}
                          className="flex items-center gap-2 text-sm text-white/90"
                        >
                          <CheckCircle2 className="h-4 w-4 text-accent shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      className="w-full font-bold bg-accent text-accent-foreground hover:bg-accent/90 mb-3 h-11"
                      asChild
                    >
                      <Link to="/kontakt">Angebot anfordern</Link>
                    </Button>
                    <Button
                      className="bg-transparent w-full font-bold border border-white/30 text-white hover:bg-white/10 hover:text-white h-11"
                      asChild
                    >
                      <a href="tel:015253181061">
                        <Phone className="h-4 w-4 mr-2" /> 01525 3181061
                      </a>
                    </Button>
                    <div className="mt-4 flex items-center justify-center gap-1.5 text-xs text-white/60">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className="h-3 w-3 text-accent fill-accent"
                        />
                      ))}
                      <span>5,0 · Google Bewertungen</span>
                    </div>
                  </div>

                  {/* Related Posts */}
                  {relatedPosts.length > 0 && (
                    <div className="bg-background p-6 rounded-sm border border-border shadow-sm">
                      <h3 className="text-base font-bold mb-5 text-foreground">
                        Weitere Ratgeber
                      </h3>
                      <div className="space-y-5">
                        {relatedPosts.map((relatedPost) => (
                          <div key={relatedPost.id} className="group">
                            <Link
                              to={`/ratgeber/${relatedPost.slug}`}
                              className="flex gap-4 items-start"
                            >
                              <div className="w-16 h-16 rounded-sm overflow-hidden shrink-0 border border-border">
                                <img
                                  src={relatedPost.imageUrl}
                                  alt={relatedPost.title}
                                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                  width={64}
                                  height={64}
                                  loading="lazy"
                                  decoding="async"
                                />
                              </div>
                              <div className="min-w-0">
                                <h4 className="font-bold text-sm line-clamp-2 group-hover:text-primary transition-colors mb-1 leading-snug">
                                  {relatedPost.title}
                                </h4>
                                <div className="text-xs text-muted-foreground">
                                  {relatedPost.date}
                                </div>
                              </div>
                            </Link>
                          </div>
                        ))}
                      </div>
                      <Link
                        to="/ratgeber"
                        className="inline-flex items-center text-primary text-sm font-bold mt-5 hover:text-accent transition-colors"
                      >
                        Alle Ratgeber ansehen{" "}
                        <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                      </Link>
                    </div>
                  )}

                  {/* Trust Signals */}
                  <div className="bg-secondary/30 p-6 rounded-sm border border-border">
                    <h3 className="text-sm font-bold text-foreground mb-4">
                      Warum Miros Haushaltsauflösung?
                    </h3>
                    <ul className="space-y-3">
                      {[
                        "Über 500 abgeschlossene Aufträge",
                        "Familienbetrieb seit 5 Jahren",
                        "Vollversichert (Betriebshaftpflicht)",
                        "Termin oft in 24h möglich",
                        "Wertanrechnung inklusive",
                      ].map((item, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-2 text-sm text-muted-foreground"
                        >
                          <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* Related Posts Section at bottom on mobile */}
        {relatedPosts.length > 0 && (
          <section className="py-12 bg-secondary/20 border-t border-border lg:hidden">
            <div className="container mx-auto px-4 md:px-6">
              <h2 className="text-xl font-bold mb-6">Weitere Ratgeber</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {relatedPosts.slice(0, 2).map((relatedPost) => (
                  <article
                    key={relatedPost.id}
                    className="bg-card rounded-sm border border-border overflow-hidden flex flex-col shadow-sm"
                  >
                    <Link
                      to={`/ratgeber/${relatedPost.slug}`}
                      className="block h-40 overflow-hidden"
                    >
                      <img
                        src={relatedPost.imageUrl}
                        alt={relatedPost.title}
                        className="w-full h-full object-cover"
                        width={400}
                        height={250}
                        loading="lazy"
                        decoding="async"
                      />
                    </Link>
                    <div className="p-4 flex flex-col flex-1">
                      <span className="text-xs font-bold uppercase text-accent mb-2">
                        {relatedPost.category}
                      </span>
                      <h3 className="text-sm font-bold line-clamp-2 mb-3">
                        {relatedPost.title}
                      </h3>
                      <Link
                        to={`/ratgeber/${relatedPost.slug}`}
                        className="inline-flex items-center text-primary text-xs font-bold mt-auto hover:text-accent transition-colors"
                      >
                        Weiterlesen <ArrowRight className="ml-1 h-3 w-3" />
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
}
